// Presentation only: this module never schedules, resumes or cancels account work.
export function remainingUntil(value, now) {
  if (!value) return null;
  const deadline = typeof value === "number" ? value * 1000 : Date.parse(value);
  if (!Number.isFinite(deadline)) return null;
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}

export function formatWait(seconds) {
  if (seconds === null || seconds === undefined) return "—";
  const value = Math.max(0, Math.ceil(seconds));
  const h = Math.floor(value / 3600);
  const m = Math.floor((value % 3600) / 60);
  const s = value % 60;
  return [ ...(h ? [String(h).padStart(2, "0")] : []),
    String(m).padStart(2, "0"), String(s).padStart(2, "0") ].join(":");
}

export function verificationReason(reason) {
  if (!reason) return "Hasil verifikasi belum diterima oleh bot.";
  if (/subscription|plan_auto_verify/.test(reason)) return "Masa aktif atau akses verifikasi akun perlu diperiksa.";
  if (reason === "vip_membership_required") return "Bot meminta akses VIP untuk akun ini.";
  if (/proxy|TUNNEL/i.test(reason)) return "Koneksi jaringan verifikasi sedang bermasalah.";
  if (/timeout|timed_out/i.test(reason)) return "Proses verifikasi belum selesai dalam batas waktu.";
  if (/response_missing|input_missing|page_not_successful/.test(reason)) return "Halaman verifikasi belum memberikan konfirmasi.";
  if (/webview_url_missing/.test(reason)) return "Tautan verifikasi belum tersedia dari bot.";
  if (/browser_temporarily_disabled/.test(reason)) return "Verifikasi otomatis sedang dijeda oleh pengelola layanan.";
  return "Hasil verifikasi belum diterima oleh bot.";
}

const NORMAL = {
  idle: ["Siap dijalankan", "Belum ada sesi memancing yang sedang berjalan.", "Tekan Mulai bot saat siap."],
  starting: ["Memulai bot", "Bot sedang menyiapkan sesi akun ini.", "Tunggu hingga proses persiapan selesai."],
  opening: ["Membuka sesi", "Bot sedang membuka sesi memancing.", "Tunggu balasan dari bot game."],
  joining: ["Mendaftar sesi", "Bot sedang mendaftarkan akun ke sesi memancing.", "Tunggu konfirmasi pendaftaran."],
  waiting: ["Menunggu sesi / balasan", "Bot tetap aktif dan sedang menunggu langkah berikutnya.", "Belum perlu tindakan dari Anda."],
  fishing: ["Sedang mancing", "Sesi memancing sedang berjalan.", "Hasil tangkapan akan diperbarui saat diterima."],
  extracting: ["Memproses extract", "Bot sedang memproses extract inventory.", "Tunggu hingga proses selesai."],
  selling: ["Menjual hasil tangkapan", "Bot sedang memproses penjualan.", "Tunggu konfirmasi dari bot game."],
  inventory: ["Memeriksa inventory", "Bot sedang membaca isi inventory.", "Tunggu hingga pemeriksaan selesai."],
  favoriting: ["Melindungi ikan", "Bot sedang menandai ikan sebagai favorit.", "Tunggu hingga proses selesai."],
  verifying: ["Status verifikasi sedang diperbarui", "Tahap verifikasi belum terkonfirmasi oleh antrean aktif.", "Tunggu pembaruan status; hubungi admin jika kondisi ini menetap."],
  paused: ["Bot dijeda", "Akun tidak menjalankan langkah baru selama jeda.", "Tekan Lanjutkan saat siap."],
  stopped: ["Bot berhenti", "Akun tidak menjalankan sesi baru.", "Tekan Mulai bot untuk menjalankan akun."],
  error: ["Bot mengalami kendala", "Proses akun terhenti karena kendala operasional.", "Hubungi admin jika kendala terus berulang."],
};

export function accountStatusView(status, telegram, now = Date.now(), receivedAt = now) {
  const result = (label, description, nextStep, tone, extra = {}) => ({
    label, description, nextStep, tone, remainingSeconds: null,
    countdownLabel: "Langkah berikutnya", blockStart: false, ...extra,
  });
  if (!status || !telegram) return result("Memuat status akun", "Sedang mengambil kondisi terbaru akun ini.", "Tunggu sebentar.", "idle", { blockStart: true });
  if (telegram.safety_blocked) return result("Akun dibatasi Telegram", "Pengaman akun aktif karena pembatasan Telegram.", "Hubungi admin untuk pemeriksaan akun sebelum menjalankan bot.", "error", { blockStart: true });
  const floodWait = remainingUntil(telegram.retry_after_epoch, now);
  if (floodWait > 0) return result("Menunggu jeda Telegram", "Telegram membatasi permintaan akun ini untuk sementara.", "Tunggu hitung mundur selesai. Jika bot masih dijeda setelahnya, tekan Lanjutkan.", "paused", {
    remainingSeconds: floodWait, countdownLabel: "Jeda Telegram", blockStart: true,
  });
  if (telegram.connected === false) return result("Telegram terputus", "Akun Telegram belum terhubung ke layanan.", "Sambungkan akun kembali dari menu Telegram.", "error", {
    blockStart: true, actionLink: { href: "/dashboard/telegram", label: "Hubungkan Telegram" },
  });
  const phase = status.verification_queue_status;
  if (phase === "PROCESSING") return result("Sedang verifikasi", "Permintaan verifikasi akun ini sedang diproses.", "Tunggu hasil verifikasi; belum perlu menekan Lanjutkan.", "verifying");
  if (phase === "QUEUED" || phase === "PREPARING") {
    const position = status.verification_queue_position;
    return result("Menunggu antrean verifikasi", position > 0
      ? `Urutan ${position} dari ${status.verification_queue_length} akun yang menunggu.`
      : "Permintaan sedang diperiksa sebelum diproses.",
    "Akun akan diproses saat tersedia slot. Waktu tunggu bergantung pada tugas sebelumnya.", "verifying");
  }
  if (phase === "COOLDOWN") {
    const remaining = remainingUntil(status.verification_retry_at, now);
    return result("Jeda percobaan verifikasi", verificationReason(status.verification_failure_reason), remaining === 0
      ? "Jeda selesai; menunggu pembaruan antrean."
      : remaining === null ? "Waktu percobaan berikutnya belum tersedia. Tunggu pembaruan status."
      : "Setelah jeda selesai, akun akan kembali masuk antrean verifikasi.", "paused", {
      remainingSeconds: remaining, countdownLabel: "Jeda verifikasi",
    });
  }
  if (["RECOVERING", "UNKNOWN"].includes(phase) || status.verification_status === "UNCONFIRMED") {
    return result("Status verifikasi belum terkonfirmasi", phase === "RECOVERING"
      ? "Permintaan sedang dipulihkan setelah layanan dimuat ulang."
      : "Belum ada konfirmasi hasil atau proses verifikasi aktif untuk akun ini.",
    "Tunggu pembaruan status; hubungi admin jika kondisi ini menetap.", "verifying");
  }
  if (["FAILED", "NEED_VERIFICATION"].includes(status.verification_status) && ["paused", "error", "verifying"].includes(status.status)) {
    if (status.verification_failure_reason === "vip_membership_required") {
      return result("Perlu akses VIP", verificationReason(status.verification_failure_reason),
        "Periksa keanggotaan VIP akun pada bot game, lalu lanjutkan setelah aksesnya aktif.", "paused");
    }
    if (/subscription|plan_auto_verify/.test(status.verification_failure_reason || "")) {
      return result("Masa aktif / akses perlu diperiksa", verificationReason(status.verification_failure_reason),
        "Periksa menu Langganan atau hubungi admin untuk memastikan akses akun aktif.", "paused", {
          actionLink: { href: "/dashboard/pricing", label: "Periksa langganan" },
        });
    }
    return result("Perlu verifikasi manual", verificationReason(status.verification_failure_reason),
      "Buka bot game di Telegram dan selesaikan verifikasi. Setelah berhasil, kembali lalu tekan Lanjutkan.", "verifying");
  }
  if (status.status === "paused" && /floodwait|telegram meminta jeda/i.test(status.last_error || status.last_message || "") && floodWait === 0) {
    return result("Jeda Telegram selesai", "Batas waktu jeda Telegram telah berakhir.", "Tekan Lanjutkan untuk menjalankan akun kembali.", "paused");
  }
  const [label, description, nextStep] = NORMAL[status.status] || NORMAL.idle;
  const deadline = remainingUntil(status.next_action_at, now);
  const elapsed = Math.max(0, Math.floor((now - receivedAt) / 1000));
  const countdown = status.countdown_seconds > 0
    ? deadline ?? Math.max(0, status.countdown_seconds - elapsed) : null;
  const pausedByUser = status.status === "paused" && /paused by user/i.test(status.last_message || "");
  return result(label, pausedByUser ? "Bot dijeda melalui tombol Jeda." : description, nextStep, status.status || "idle", {
    remainingSeconds: countdown,
  });
}
